/**
 * The decisions a closed month supports: what is left to move, how deep the
 * reserve is, and where the money realistically went. Every figure is computed
 * from the published month's own transactions (see src/lib/decisions.ts).
 */
import { useApp } from './context';
import { useAsync } from './hooks';
import { ErrorNote, Loading, Panel } from './ui';
import { useSealedMoney } from './sealed';
import { categoryCuts, isMaterialCut, MATERIAL_CUT, monthVerdict, monthsToClose, reserve, topPayees, RESERVE_LATER_MONTHS, RESERVE_TARGET_MONTHS } from '../lib/decisions';
import { dateLabel, naira, nairaShort, shortMonthLabel, monthLabel } from '../lib/format';
import { monthKey, previousPublished, publishedMonths } from '../lib/periods';
import { navigate } from '../lib/route';
import type { Month } from '../data/types';

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

function Line({ label, value, sub, strong, minus }: { label: string; value: number | null; sub?: string; strong?: boolean; minus?: boolean }) {
  return (
    <li className={strong ? 'ledger-total' : ''}>
      <span>{label}{sub && <small>{sub}</small>}</span>
      <b>{value === null ? '—' : `${minus && value > 0 ? '−' : ''}${naira(value)}`}</b>
    </li>
  );
}

export function DecisionPanels({ month }: { month: Month }) {
  const { repo, base } = useApp();
  const published = publishedMonths(base.months);
  const p1 = previousPublished(base.months, month.month_start);
  const p2 = p1 ? previousPublished(base.months, p1.month_start) : null;
  const data = useAsync(async () => {
    const [cats, ...priors] = await Promise.all([
      repo.categoryMonths(month.id),
      ...[p1, p2].filter((m): m is Month => Boolean(m)).map((m) => repo.categoryMonths(m.id)),
    ]);
    return { cats, priors };
  }, [month.id]);

  const target = base.openPlan?.investment_target ?? month.investment_target;
  const label = monthLabel(month.month_start);
  const sinceThis = published.filter((m) => m.month_start <= month.month_start);
  const r = reserve(base.accounts, sinceThis);

  const sealed = useSealedMoney(month);
  if (data.error || sealed.error) return <ErrorNote message={data.error ?? sealed.error!} onRetry={() => { data.reload(); sealed.reload(); }} />;
  if (!data.data || !sealed.data) return <Panel title={`${label}: decision`}><Loading /></Panel>;

  const m = sealed.data.money;
  const cuts = categoryCuts(data.data.cats, data.data.priors).filter(isMaterialCut).slice(0, 3);
  const payees = topPayees(sealed.data.txs, 8);
  const backToCeiling = cuts.reduce((s, c) => s + c.overCap, 0);
  const evidence = (category?: string) => navigate({ section: 'spend', month: monthKey(month.month_start), category });

  return (
    <>
      <Panel title={`${label}: what was left to move`} meta="From published transactions" className="decision">
        <ul className="ledger">
          <Line label="Income" sub="salary and allowances" value={m.income} />
          <Line label="Personal spend" sub={`fixed ${nairaShort(m.fixed)} · lifestyle ${nairaShort(m.lifestyle)} · other ${nairaShort(m.otherSpend)}`} value={m.personal} minus />
          <Line label="Migrate World" sub={m.businessOutside > 0 ? `business capital, includes ${nairaShort(m.businessOutside)} paid outside your accounts` : 'business capital, tracked apart'} value={m.business} minus />
          <Line label={m.available !== null && m.available < 0 ? 'Spent more than earned' : 'Spare after spending'} value={m.available === null ? null : Math.abs(m.available)} strong />
          <Line label="Invested" sub="moved into investments, still yours" value={m.invested} minus />
          <Line label={m.leftover !== null && m.leftover < 0 ? 'Total taken from savings' : 'Left over'} sub={m.leftover !== null && m.leftover < 0 ? 'overspend plus investing' : undefined} value={m.leftover === null ? null : Math.abs(m.leftover)} strong />
        </ul>
        <p className={`verdict${m.available !== null && m.available < 0 ? ' verdict-bad' : ''}`}>
          {(() => {
            const v = monthVerdict(m);
            switch (v.kind) {
              case 'unknown': return `No income is recorded for ${label}, so the surplus is unknown. Check that salary landed in an account whose statement was uploaded.`;
              case 'left': return `${nairaShort(v.amount)} of ${label}'s income is still sitting in your bank accounts, already counted in your reserve. Moving it to GT Money Market keeps it there and counts it as invested.`;
              case 'investedBeyond': return `${label} left ${nairaShort(v.spare)} spare and you invested ${nairaShort(v.invested)}, so ${nairaShort(v.fromSavings)} of the investing came from savings. It is still yours, now in investments.`;
              case 'overspent': return `${label} spent ${nairaShort(v.amount)} more than it earned. That is the number to fix.${v.invested > 0 ? ` The ${nairaShort(v.invested)} you invested also came from savings, but it is still yours.` : ''}`;
            }
          })()}
        </p>
        {m.otherInflows > 0 && <p className="muted small">Other money in ({nairaShort(m.otherInflows)}: refunds, reimbursements, unrecognised credits) is not counted as income.</p>}
      </Panel>

      <Panel title="Reserve" meta={r.newestAsOf ? `balances as of ${dateLabel(r.newestAsOf)}` : undefined}>
        <div className="stats-3">
          <div className="stat"><span className="stat-label">Bank + money market</span><span className="stat-value">{nairaShort(r.liquid)}</span><span className="stat-foot">{plural(r.accounts, 'bank or money-market account')}</span></div>
          <div className="stat"><span className="stat-label">Months covered</span><span className="stat-value">{r.monthsCovered === null ? '—' : r.monthsCovered.toFixed(1)}</span><span className="stat-foot">at {nairaShort(r.avgMonthlySpend)} average monthly spend</span></div>
          <div className="stat"><span className="stat-label">{RESERVE_TARGET_MONTHS}-month target</span><span className="stat-value">{nairaShort(r.target)}</span><span className="stat-foot">{r.gap ? `${nairaShort(r.gap)} to go` : r.gap === 0 ? 'Reached' : '—'}</span></div>
        </div>
        {r.gap !== null && r.gap > 0 && target ? (
          <p className="muted">At {nairaShort(target)} a month the gap closes in about {plural(monthsToClose(r.gap, target)!, 'month')}; at {nairaShort(target / 2)}, about {plural(monthsToClose(r.gap, target / 2)!, 'month')}. Spending less closes it from both ends: the target is measured in months of spend.</p>
        ) : null}
        {r.stale.length > 0 && <p className="muted small stale">Not current: {r.stale.map((x) => `${x.name} (last updated ${dateLabel(x.asOf)})`).join(', ')}. Update it on Wealth or upload its statement.</p>}
        <p className="muted small">Counts naira in bank accounts and GT Money Market, so moving between them does not change it. ARM, property, Migrate World and USD are not counted. Next goal after {RESERVE_TARGET_MONTHS} months: {RESERVE_LATER_MONTHS}.</p>
      </Panel>

      <Panel title="Where to cut" meta={`${shortMonthLabel(month.month_start)}${p1 ? ` vs ${p2 ? '2-month' : 'prior'} average` : ''} · ${nairaShort(MATERIAL_CUT)}+ only`}>
        {cuts.length ? (
          <>
            <ul className="ledger">
              {cuts.map((c) => (
                <li key={c.name}>
                  <button className="link" onClick={() => evidence(c.name)}>{c.name}</button>
                  <span className="ledger-note">
                    {naira(c.actual)}
                    {c.overCap > 0 && <small className="delta-bad">{nairaShort(c.overCap)} over ceiling</small>}
                    {c.aboveAvg > 0 && <small>{nairaShort(c.aboveAvg)} above its average</small>}
                  </span>
                </li>
              ))}
            </ul>
            {backToCeiling > 0 && <p className="verdict">Bringing these back to their ceilings frees about {nairaShort(backToCeiling)} a month, or {nairaShort(backToCeiling * 12)} a year, for GT Money Market.</p>}
          </>
        ) : <p className="muted">No category ran {nairaShort(MATERIAL_CUT)} or more over its ceiling or its recent average.</p>}

        <details className="payees">
        <summary>Biggest payees</summary>
        <ul className="ledger">
          {payees.map((p) => (
            <li key={p.key}>
              <span>{p.key}<small>{p.category ?? 'Uncategorised'} · {plural(p.count, 'payment')}</small></span>
              <b>{naira(p.total)}</b>
            </li>
          ))}
        </ul>
        </details>
      </Panel>
    </>
  );
}
